import { apiRecursive, ApiSmpAccountCredentials, ApiSmpPage } from "@smp/api";
import produce from "immer";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { IContextSmp } from "./smp.types";

const ContextSmp = createContext<IContextSmp>(null as unknown as IContextSmp);

export const ContextProviderSmp = ({ children }: PropsWithChildren<{}>) => {
  const [smpPagesMap, setSmpPagesMap] = useState(new Map());

  const [smpPageProxiesMap, setSmpPageProxiesMap] = useState(new Map());

  const [smpStreamDestinationsMap, setSmpStreamDestinationsMap] = useState<
    IContextSmp["state"]["smpStreamDestinationsMap"]
  >(new Map());

  const [smpStreamSourcesMap, setSmpStreamSourcesMap] = useState<
    IContextSmp["state"]["smpStreamSourcesMap"]
  >(new Map());

  const [smpStreamsMap, setSmpStreamsMap] = useState<
    IContextSmp["state"]["smpStreamsMap"]
  >(new Map());

  const smpAccountCredentialsGet: IContextSmp["actions"]["smpAccountCredentialsGet"] =
    useCallback(async (...args) => {
      const accountCredentialsList = (
        await ApiSmpAccountCredentials.list(...args)
      ).results;
      return accountCredentialsList;
    }, []);

  const smpAccountCredentialsPatch: IContextSmp["actions"]["smpAccountCredentialsPatch"] =
    useCallback(async ({ id, smpAccountCredentialPartial }) => {
      const smpAccountCredentialUpdated =
        await ApiSmpAccountCredentials.updatePartial({
          id,
          smpAccountCredentialPartial,
        });
      return smpAccountCredentialUpdated;
    }, []);

  const smpPageGet: IContextSmp["actions"]["smpPageGet"] = useCallback(
    async ({ id }) => {
      const smpPage = await ApiSmpPage.retrieveById({ path: { id } });
      setSmpPagesMap((smpPagesMap) =>
        produce(smpPagesMap, (draft) => {
          draft.set(smpPage.id, smpPage);
        }),
      );
      setSmpPageProxiesMap((smpPageProxiesMap) =>
        produce(smpPageProxiesMap, (draft) => {
          draft.set(smpPage.medium + ":" + smpPage.external_id, smpPage.id);
        }),
      );
      return smpPage;
    },
    [],
  );

  const smpPageByExternalIdGet: IContextSmp["actions"]["smpPageByExternalIdGet"] =
    useCallback(async ({ medium__external_id }) => {
      const smpPage = await ApiSmpPage.retrieveByExternalId({
        path: { medium__external_id },
      });
      setSmpPagesMap((smpPagesMap) =>
        produce(smpPagesMap, (draft) => {
          draft.set(smpPage.id, smpPage);
        }),
      );
      setSmpPageProxiesMap((smpPageProxiesMap) =>
        produce(smpPageProxiesMap, (draft) => {
          draft.set(smpPage.medium + ":" + smpPage.external_id, smpPage.id);
        }),
      );
      return smpPage;
    }, []);

  const smpPagesGet: IContextSmp["actions"]["smpPagesGet"] = useCallback(
    async ({ id__in, medium_external_id__in }) => {
      const smpPagesList = await apiRecursive(ApiSmpPage.listById, [
        {
          query: {
            id__in,
            medium_external_id__in,
          },
        },
      ]);
      setSmpPagesMap((smpPagesMap) =>
        produce(smpPagesMap, (draft) => {
          smpPagesList.forEach((smpPage) => {
            draft.set(smpPage.id, smpPage);
          });
        }),
      );
      setSmpPageProxiesMap((smpPageProxiesMap) =>
        produce(smpPageProxiesMap, (draft) => {
          smpPagesList.forEach((smpPage) => {
            draft.set(smpPage.medium + ":" + smpPage.external_id, smpPage.id);
          });
        }),
      );
      return smpPagesList;
    },
    [],
  );

  const smpPagesSubscriptionsGet: IContextSmp["actions"]["smpPagesSubscriptionsGet"] =
    useCallback(async (...args) => {
      const subjects = await ApiSmpPage.Subscription.list(...args);
      return subjects;
    }, []);

  return useMemo(
    () => (
      <ContextSmp.Provider
        value={{
          actions: {
            setSmpStreamDestinationsMap,
            setSmpStreamSourcesMap,
            setSmpStreamsMap,
            smpAccountCredentialsGet,
            smpAccountCredentialsPatch,
            smpPageByExternalIdGet,
            smpPageGet,
            smpPagesGet,
            smpPagesSubscriptionsGet,
          },
          state: {
            smpPageProxiesMap,
            smpPagesMap,
            smpStreamDestinationsMap,
            smpStreamSourcesMap,
            smpStreamsMap,
          },
        }}
      >
        {children}
      </ContextSmp.Provider>
    ),
    [
      smpAccountCredentialsGet,
      smpAccountCredentialsPatch,
      smpPageByExternalIdGet,
      smpPageGet,
      smpPagesGet,
      smpPagesSubscriptionsGet,
      smpPageProxiesMap,
      smpPagesMap,
      smpStreamsMap,
      smpStreamDestinationsMap,
      smpStreamSourcesMap,
      children,
    ],
  );
};

export const useContextSmp = () => useContext(ContextSmp);
